// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import animationVideo from '../assets/crypto-logos/animation.mp4';
// Logo now served from public directory
import { TrendingUp, BarChart3, Shield, Target, Activity, ArrowRight, CheckCircle2, Brain, Sun, Moon, Trophy } from 'lucide-react';
import AuthModal from '../components/AuthModal';

const NewHome: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedTool, setSelectedTool] = useState('aiagents');
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');

  // Check URL parameters for opening login modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const showLogin = params.get('showLogin');
    const mode = params.get('mode');

    if (showLogin === 'true') {
      setAuthModalMode(mode === 'register' ? 'register' : 'login');
      setAuthModalOpen(true);
      // Clean up URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Navigate with auth check
  const handleNavigate = (path: string) => {
    if (user) {
      navigate(path);
    } else {
      setAuthModalMode('login');
      setAuthModalOpen(true);
    }
  };

  // Scroll animation effect - triggers every time for all animated elements
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in-view');
          } else {
            entry.target.classList.remove('animate-in-view');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all elements with scroll-animate and text-fade-in classes
    const animatedElements = document.querySelectorAll('.scroll-animate, .text-fade-in');
    animatedElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animatedElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950 text-zinc-50' : 'bg-gray-50 text-gray-900'}`}>
      {/* Simple Header - No Navigation Menu */}
      <nav className={`sticky top-0 z-50 backdrop-blur ${isDark ? 'bg-black/30 border-b border-white/10' : 'bg-white/95 border-b border-gray-200'}`}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center hover:opacity-80 transition-opacity cursor-pointer"
            >
              <img src="/investverse-logo.svg" alt="InvestVerse.AI" className="h-8 w-auto" />
            </button>
            <div className="flex items-center gap-3">
              {/* Contest Button - visible for all users */}
              <button
                onClick={() => navigate('/contest')}
                className="px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 bg-gradient-to-r from-[#00ffe7]/10 to-purple-500/10 border border-[#00ffe7]/30 text-[#00ffe7] hover:from-[#00ffe7]/20 hover:to-purple-500/20 hover:border-[#00ffe7]/50 hover:scale-105 hover:shadow-lg hover:shadow-[#00ffe7]/20 flex items-center gap-2 group"
              >
                <Trophy className="h-4 w-4 text-[#00ffe7] animate-trophy-bounce flex-shrink-0" />
                <span className="hidden sm:inline">Contest</span>
              </button>

              {user ? (
                // Logged-in user: Show "Go to Dashboard" button (goes to AI Agents page)
                <button
                  onClick={() => navigate('/agent-analysis')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                    isDark ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                  }`}
                >
                  Go to Dashboard
                </button>
              ) : (
                // Not logged in: Show Login and Get Started buttons
                <>
                  <button
                    onClick={() => {
                      setAuthModalMode('login');
                      setAuthModalOpen(true);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      isDark ? 'text-white/70 hover:text-white hover:bg-white/5' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Login
                  </button>
                  <button
                    onClick={() => {
                      setAuthModalMode('register');
                      setAuthModalOpen(true);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      isDark ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                    }`}
                  >
                    Get Started
                  </button>
                </>
              )}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-xl transition-all duration-200 ${
                  isDark ? 'border border-white/10 hover:bg-white/10' : 'border border-gray-200 hover:bg-gray-100'
                }`}
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className={`${isDark ? 'text-zinc-50' : 'text-gray-900'}`} style={{ background: 'transparent' }}>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center overflow-visible" style={{ background: '#000000' }}>
          {/* Container with grid layout: left text, right video */}
          <div className="relative z-10 w-full max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch min-h-screen lg:h-screen" style={{ overflow: 'visible', background: 'transparent' }}>
            {/* Left: Text Content */}
            <div className="flex items-center justify-center px-6 sm:px-8 lg:px-12 py-12 lg:py-0" style={{ paddingLeft: 'clamp(24px, 5vw, 80px)', paddingBottom: 'clamp(24px, 5vw, 80px)' }}>
              <div className="space-y-4 sm:space-y-6 max-w-xl w-full">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">
                  Stop Guessing.<br />Start Winning.
                </h1>

                <p className="text-base sm:text-lg lg:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed">
                  While others analyze one chart, you'll have 10 AI agents working 24/7 to spot opportunities, assess risk, and predict market moves before they happen.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button
                    onClick={() => handleNavigate('/agent-analysis')}
                    className="px-6 sm:px-8 py-3 sm:py-4 bg-[#00ffe7] text-black font-semibold text-base sm:text-lg rounded-lg hover:bg-[#00d4cc] transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#00ffe7]/50 text-center w-full sm:w-auto"
                  >
                    Start Free Now →
                  </button>

                  <button
                    onClick={() => handleNavigate('/agent-analysis')}
                    className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-white/20 text-white font-semibold text-base sm:text-lg rounded-lg hover:bg-white/10 hover:border-white/40 transition-all duration-300 hover:scale-105 text-center w-full sm:w-auto"
                  >
                    See It In Action
                  </button>
                </div>

                <p className="text-xs sm:text-sm text-white/60 mt-3 sm:mt-4 text-center sm:text-left">
                  ✓ No credit card required  ✓ 2-minute setup  ✓ Cancel anytime
                </p>
              </div>
            </div>

            {/* Right: Video - Full Height with Vignette */}
            <div className="relative w-full h-64 sm:h-96 lg:h-full flex items-stretch" style={{ background: 'transparent' }}>
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              >
                <source src={animationVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              {/* Left edge vignette overlay for smooth transition */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.7) 10%, rgba(0, 0, 0, 0) 30%)',
                  zIndex: 1
                }}
              ></div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 sm:py-16 lg:py-20 relative overflow-hidden">
          <div className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Stat Card 1 */}
              <div className="relative">
                <div
                  className={`scroll-animate relative px-3 sm:px-6 py-6 sm:py-12 rounded-lg border ${
                    isDark ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-lg'
                  }`}
                  style={{
                    minHeight: '200px'
                  }}
                >
                  <div className="relative z-10 text-center flex flex-col justify-between h-full py-4 sm:py-8">
                    <div
                      className="text-4xl sm:text-5xl lg:text-7xl font-bold"
                      style={{
                        fontFamily: '"Orbitron", monospace',
                        color: isDark ? '#06b6d4' : '#0891b2'
                      }}
                    >
                      100+
                    </div>
                    <div className={`text-[10px] sm:text-xs uppercase tracking-wider leading-tight mt-auto ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      Coins Analyzed Every Hour
                    </div>
                  </div>
                </div>
              </div>

              {/* Stat Card 2 */}
              <div className="relative">
                <div
                  className={`scroll-animate relative px-3 sm:px-6 py-6 sm:py-12 rounded-lg border ${
                    isDark ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-lg'
                  }`}
                  style={{
                    minHeight: '200px'
                  }}
                >
                  <div className="relative z-10 text-center flex flex-col justify-between h-full py-4 sm:py-8">
                    <div
                      className="text-4xl sm:text-5xl lg:text-7xl font-bold"
                      style={{
                        fontFamily: '"Orbitron", monospace',
                        color: isDark ? '#8b5cf6' : '#7c3aed'
                      }}
                    >
                      10K+
                    </div>
                    <div className={`text-[10px] sm:text-xs uppercase tracking-wider leading-tight mt-auto ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      Users Catered To Daily
                    </div>
                  </div>
                </div>
              </div>

              {/* Stat Card 3 */}
              <div className="relative">
                <div
                  className={`scroll-animate relative px-3 sm:px-6 py-6 sm:py-12 rounded-lg border ${
                    isDark ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-lg'
                  }`}
                  style={{
                    minHeight: '200px'
                  }}
                >
                  <div className="relative z-10 text-center flex flex-col justify-between h-full py-4 sm:py-8">
                    <div
                      className="text-4xl sm:text-5xl lg:text-7xl font-bold"
                      style={{
                        fontFamily: '"Orbitron", monospace',
                        color: isDark ? '#06b6d4' : '#0891b2'
                      }}
                    >
                      4hr
                    </div>
                    <div className={`text-[10px] sm:text-xs uppercase tracking-wider leading-tight mt-auto ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      Real-Time Update Frequency
                    </div>
                  </div>
                </div>
              </div>

              {/* Stat Card 4 */}
              <div className="relative">
                <div
                  className={`scroll-animate relative px-3 sm:px-6 py-6 sm:py-12 rounded-lg border ${
                    isDark ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-lg'
                  }`}
                  style={{
                    minHeight: '200px'
                  }}
                >
                  <div className="relative z-10 text-center flex flex-col justify-between h-full py-4 sm:py-8">
                    <div
                      className="text-4xl sm:text-5xl lg:text-7xl font-bold"
                      style={{
                        fontFamily: '"Orbitron", monospace',
                        color: isDark ? '#f97316' : '#ea580c'
                      }}
                    >
                      15+
                    </div>
                    <div className={`text-[10px] sm:text-xs uppercase tracking-wider leading-tight mt-auto ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      Technical Indicators Tracked
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Tools Showcase Section */}
        <section className="py-12 sm:py-16 lg:py-20">
          <div className="max-w-[960px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Title */}
            <div className="text-center mb-8 sm:mb-12">
              <h2
                className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}
                style={{
                  fontFamily: '"Orbitron", monospace',
                  letterSpacing: '0.05em'
                }}
              >
                Everything You Need to Trade Smarter
              </h2>
              <div className="h-1 w-32 bg-gradient-to-r from-cyan-500 to-purple-500 mx-auto mt-4"></div>
              <p className={`text-lg mt-6 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                From AI-powered insights to live market data—all in one dashboard
              </p>
            </div>
            <div
              className="scroll-animate rounded-xl relative p-[3px]"
              style={{
                background: isDark
                  ? 'linear-gradient(180deg, rgba(228, 228, 231, 0.6) 0%, rgba(161, 161, 170, 0.5) 30%, rgba(113, 113, 122, 0.4) 70%, rgba(63, 63, 70, 0.7) 100%)'
                  : 'linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(229, 231, 235, 0.7) 30%, rgba(156, 163, 175, 0.6) 70%, rgba(107, 114, 128, 0.8) 100%)',
                boxShadow: isDark
                  ? `
                    0 0 40px rgba(255, 255, 255, 0.15),
                    0 25px 50px -12px rgba(0, 0, 0, 0.7)
                  `
                  : `
                    0 0 40px rgba(0, 0, 0, 0.08),
                    0 25px 50px -12px rgba(0, 0, 0, 0.25)
                  `,
                filter: isDark ? 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.1))' : 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.05))'
              }}
            >
              <div
                className={`rounded-[10px] relative overflow-hidden ${isDark ? 'bg-zinc-900' : 'bg-white'}`}
              >
              <div className="flex gap-0">
                {/* Left Navigation Column */}
                <div
                  className={`w-56 flex-shrink-0 border-r relative ${
                    isDark ? 'border-white/10' : 'border-gray-200'
                  }`}
                  style={{
                    background: isDark
                      ? 'linear-gradient(180deg, rgba(39, 39, 42, 0.6) 0%, rgba(24, 24, 27, 0.8) 100%)'
                      : 'linear-gradient(180deg, rgba(249, 250, 251, 0.8) 0%, rgba(243, 244, 246, 1) 100%)'
                  }}
                >
                  <div className="py-6 space-y-1">
                  {/* AI Agents */}
                  <button
                    onClick={() => setSelectedTool('aiagents')}
                    className={`w-full text-left px-6 py-4 transition-all duration-300 ${
                      selectedTool === 'aiagents'
                        ? isDark
                          ? 'bg-cyan-500/10 border-l-4 border-cyan-500 text-cyan-400'
                          : 'bg-cyan-50 border-l-4 border-cyan-600 text-cyan-700'
                        : isDark
                          ? 'text-white/70 hover:bg-zinc-800/50 hover:text-white border-l-4 border-transparent'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 border-l-4 border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Brain className="h-5 w-5" />
                      <span className="font-medium">AI Agents</span>
                    </div>
                  </button>

                  {/* Dashboard */}
                  <button
                    onClick={() => setSelectedTool('dashboard')}
                    className={`w-full text-left px-6 py-4 transition-all duration-300 ${
                      selectedTool === 'dashboard'
                        ? isDark
                          ? 'bg-purple-500/10 border-l-4 border-purple-500 text-purple-400'
                          : 'bg-purple-50 border-l-4 border-purple-600 text-purple-700'
                        : isDark
                          ? 'text-white/70 hover:bg-zinc-800/50 hover:text-white border-l-4 border-transparent'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 border-l-4 border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Activity className="h-5 w-5" />
                      <span className="font-medium">Dashboard</span>
                    </div>
                  </button>

                  {/* Coin Search */}
                  <button
                    onClick={() => setSelectedTool('coinsearch')}
                    className={`w-full text-left px-6 py-4 transition-all duration-300 ${
                      selectedTool === 'coinsearch'
                        ? isDark
                          ? 'bg-orange-500/10 border-l-4 border-orange-500 text-orange-400'
                          : 'bg-orange-50 border-l-4 border-orange-600 text-orange-700'
                        : isDark
                          ? 'text-white/70 hover:bg-zinc-800/50 hover:text-white border-l-4 border-transparent'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 border-l-4 border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Target className="h-5 w-5" />
                      <span className="font-medium">Coin Search</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Right Content Area */}
              <div className="flex-1 p-8">
                <div className="relative">
                  {/* AI Agents Content */}
                  {selectedTool === 'aiagents' && (
                    <div className="animate-fadeIn">
                      {/* Top Container - Split Left/Right */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Left - Image */}
                        <div
                          className={`rounded-lg overflow-hidden ${
                            isDark ? 'bg-zinc-800/50 border border-white/5' : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-blue-500/10 to-purple-500/10">
                            <div className="text-center p-8">
                              <Brain className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                              <div className={`text-lg ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                                AI Agents Analysis
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right - Title */}
                        <div className="flex flex-col justify-center">
                          <h3
                            className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
                            style={{
                              fontFamily: '"Orbitron", monospace',
                              letterSpacing: '0.05em'
                            }}
                          >
                            10 Specialized AI Agents
                          </h3>
                          <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-purple-500 mb-4"></div>

                          {/* View Button */}
                          <button
                            onClick={() => handleNavigate('/agent-analysis')}
                            className={`px-6 py-3 border-2 transition-all duration-300 relative overflow-hidden group ${
                              isDark
                                ? 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10'
                                : 'border-blue-600 text-blue-700 hover:bg-blue-50'
                            }`}
                            style={{
                              fontFamily: '"Orbitron", monospace',
                              fontWeight: '500'
                            }}
                          >
                            <span className="relative z-10 flex items-center gap-2">
                              View AI Agents
                              <ArrowRight className="h-4 w-4" />
                            </span>
                            {/* Holographic scanline effect on hover */}
                            <div
                              className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity pointer-events-none"
                              style={{
                                background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(59, 130, 246, 0.2) 2px, rgba(59, 130, 246, 0.2) 4px)',
                                animation: 'scanline-move 3s linear infinite'
                              }}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Bottom Container - Explanation */}
                      <div
                        className={`rounded-lg p-6 ${
                          isDark ? 'bg-zinc-800/30 border border-white/5' : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <p className={`text-lg leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          Get instant analysis from 10 AI specialists that would normally cost thousands per month. They scan markets 24/7,
                          predict trends, evaluate risk, and tell you exactly what to do next—in plain English. No jargon, no confusion.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Dashboard Content */}
                  {selectedTool === 'dashboard' && (
                    <div className="animate-fadeIn">
                      {/* Top Container - Split Left/Right */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Left - Image */}
                        <div
                          className={`rounded-lg overflow-hidden ${
                            isDark ? 'bg-zinc-800/50 border border-white/5' : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-blue-500/10 to-purple-500/10">
                            <div className="text-center p-8">
                              <Activity className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                              <div className={`text-lg ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                                Dashboard Preview
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right - Title */}
                        <div className="flex flex-col justify-center">
                          <h3
                            className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
                            style={{
                              fontFamily: '"Orbitron", monospace',
                              letterSpacing: '0.05em'
                            }}
                          >
                            Comprehensive Dashboard
                          </h3>
                          <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-purple-500 mb-4"></div>

                          {/* View Button */}
                          <button
                            onClick={() => handleNavigate('/overview')}
                            className={`px-6 py-3 border-2 transition-all duration-300 relative overflow-hidden group ${
                              isDark
                                ? 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10'
                                : 'border-purple-600 text-purple-700 hover:bg-purple-50'
                            }`}
                            style={{
                              fontFamily: '"Orbitron", monospace',
                              fontWeight: '500'
                            }}
                          >
                            <span className="relative z-10 flex items-center gap-2">
                              View Dashboard
                              <ArrowRight className="h-4 w-4" />
                            </span>
                            {/* Holographic scanline effect on hover */}
                            <div
                              className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity pointer-events-none"
                              style={{
                                background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(168, 85, 247, 0.2) 2px, rgba(168, 85, 247, 0.2) 4px)',
                                animation: 'scanline-move 3s linear infinite'
                              }}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Bottom Container - Explanation */}
                      <div
                        className={`rounded-lg p-6 ${
                          isDark ? 'bg-zinc-800/30 border border-white/5' : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <p className={`text-lg leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          See everything that matters on one screen. Live prices, trend indicators, market sentiment, and risk scores—
                          updated every minute. Stop switching between 10 different tabs. Make faster, smarter decisions.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Coin Search Content */}
                  {selectedTool === 'coinsearch' && (
                    <div className="animate-fadeIn">
                      {/* Top Container - Split Left/Right */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Left - Image */}
                        <div
                          className={`rounded-lg overflow-hidden ${
                            isDark ? 'bg-zinc-800/50 border border-white/5' : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-orange-500/10 to-yellow-500/10">
                            <div className="text-center p-8">
                              <Target className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                              <div className={`text-lg ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                                Coin Search Preview
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right - Title */}
                        <div className="flex flex-col justify-center">
                          <h3
                            className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
                            style={{
                              fontFamily: '"Orbitron", monospace',
                              letterSpacing: '0.05em'
                            }}
                          >
                            Smart Coin Discovery
                          </h3>
                          <div className="h-1 w-24 bg-gradient-to-r from-orange-500 to-yellow-500 mb-4"></div>

                          {/* View Button */}
                          <button
                            onClick={() => handleNavigate('/search')}
                            className={`px-6 py-3 border-2 transition-all duration-300 relative overflow-hidden group ${
                              isDark
                                ? 'border-orange-500/50 text-orange-400 hover:bg-orange-500/10'
                                : 'border-orange-600 text-orange-700 hover:bg-orange-50'
                            }`}
                            style={{
                              fontFamily: '"Orbitron", monospace',
                              fontWeight: '500'
                            }}
                          >
                            <span className="relative z-10 flex items-center gap-2">
                              View Coin Search
                              <ArrowRight className="h-4 w-4" />
                            </span>
                            {/* Holographic scanline effect on hover */}
                            <div
                              className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity pointer-events-none"
                              style={{
                                background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(249, 115, 22, 0.2) 2px, rgba(249, 115, 22, 0.2) 4px)',
                                animation: 'scanline-move 3s linear infinite'
                              }}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Bottom Container - Explanation */}
                      <div
                        className={`rounded-lg p-6 ${
                          isDark ? 'bg-zinc-800/30 border border-white/5' : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <p className={`text-lg leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          Find your next winning trade in seconds. Filter 100+ coins by performance, risk level, and momentum.
                          Get instant AI analysis on any coin—no research required. Spot hidden gems before the crowd.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            </div>
            </div>
          </div>
        </section>

        {/* Add fadeIn animation to global styles */}
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-fadeIn {
            animation: fadeIn 0.4s ease-out;
          }

          /* Scroll animation for cards */
          .scroll-animate {
            opacity: 1;
            transform: perspective(1200px) rotateX(0deg) rotateY(0deg) translateY(0);
            transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
            transform-origin: center center;
          }

          .scroll-animate:not(.animate-in-view) {
            opacity: 0;
            transform: perspective(1200px) rotateX(20deg) rotateY(5deg) translateY(40px);
          }

          .scroll-animate.animate-in-view {
            opacity: 1;
            transform: perspective(1200px) rotateX(0deg) rotateY(0deg) translateY(0);
          }

          /* Text fade-in animation for product showcase */
          .text-fade-in {
            opacity: 0.3;
            transform: translateY(10px);
            transition: opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s;
          }

          .text-fade-in.animate-in-view {
            opacity: 1;
            transform: translateY(0);
          }
        `}</style>

        {/* Features Overview Section */}
        <section className="py-20">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2
                className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
                style={{
                  fontFamily: '"Orbitron", monospace',
                  letterSpacing: '0.05em'
                }}
              >
                Why Traders Are Switching to InvestVerse
              </h2>
              <p className={`text-xl ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                Get the edge that professional traders pay thousands for
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ perspective: '1000px' }}>
              {/* Feature 1 - Row 1 */}
              <div
                className={`rounded-lg p-6 transition-all duration-300 scroll-animate ${
                isDark
                  ? 'bg-zinc-900 border border-white/10 hover:border-cyan-500/50'
                  : 'bg-white border border-gray-200 hover:border-cyan-500/50 shadow-lg'
              }`}>
                <div className="mb-4">
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mb-4">
                    <Brain className="h-6 w-6 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    AI Agents
                  </h3>
                </div>
                <p className={`text-base leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Like having 10 expert analysts working for you around the clock—spotting opportunities while you sleep
                </p>
              </div>

              {/* Feature 2 - Row 1 */}
              <div
                className={`rounded-lg p-6 transition-all duration-300 scroll-animate ${
                isDark
                  ? 'bg-zinc-900 border border-white/10 hover:border-purple-500/50'
                  : 'bg-white border border-gray-200 hover:border-purple-500/50 shadow-lg'
              }`}>
                <div className="mb-4">
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mb-4">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Hedge Analysis
                  </h3>
                </div>
                <p className={`text-base leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Find coins that perform when BTC/ETH fall, perfect for portfolio diversification and risk management
                </p>
              </div>

              {/* Feature 3 - Row 1 */}
              <div
                className={`rounded-lg p-6 transition-all duration-300 scroll-animate ${
                isDark
                  ? 'bg-zinc-900 border border-white/10 hover:border-orange-500/50'
                  : 'bg-white border border-gray-200 hover:border-orange-500/50 shadow-lg'
              }`}>
                <div className="mb-4">
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                    <Activity className="h-6 w-6 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Technical Indicators
                  </h3>
                </div>
                <p className={`text-base leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  RSI, MACD, Bollinger Bands, Moving Averages and more—all calculated in real-time for accurate analysis
                </p>
              </div>

              {/* Feature 4 - Row 2 */}
              <div
                className={`rounded-lg p-6 transition-all duration-300 scroll-animate ${
                isDark
                  ? 'bg-zinc-900 border border-white/10 hover:border-emerald-500/50'
                  : 'bg-white border border-gray-200 hover:border-emerald-500/50 shadow-lg'
              }`}>
                <div className="mb-4">
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center mb-4">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Price Comparison
                  </h3>
                </div>
                <p className={`text-base leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Compare multiple cryptocurrencies side-by-side with synchronized charts and detailed metrics
                </p>
              </div>

              {/* Feature 5 - Row 2 */}
              <div
                className={`rounded-lg p-6 transition-all duration-300 scroll-animate ${
                isDark
                  ? 'bg-zinc-900 border border-white/10 hover:border-cyan-500/50'
                  : 'bg-white border border-gray-200 hover:border-cyan-500/50 shadow-lg'
              }`}>
                <div className="mb-4">
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center mb-4">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Market Trends
                  </h3>
                </div>
                <p className={`text-base leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Track market-wide trends and sentiment to identify the best opportunities for profitable trades
                </p>
              </div>

              {/* Feature 6 - Row 2 */}
              <div
                className={`rounded-lg p-6 transition-all duration-300 scroll-animate ${
                isDark
                  ? 'bg-zinc-900 border border-white/10 hover:border-pink-500/50'
                  : 'bg-white border border-gray-200 hover:border-pink-500/50 shadow-lg'
              }`}>
                <div className="mb-4">
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center mb-4">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Smart Alerts
                  </h3>
                </div>
                <p className={`text-base leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Set custom alerts for price movements, technical signals, and market conditions to never miss an opportunity
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {/* Step 1 */}
              <div className="scroll-animate text-center flex flex-col items-center">
                {/* Glassmorphic Icon */}
                <div
                  className="h-24 w-24 rounded-2xl mb-6 flex items-center justify-center backdrop-blur-xl border shadow-lg"
                  style={{
                    background: isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.8)',
                    borderColor: isDark
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(209, 213, 219, 0.3)',
                    boxShadow: isDark
                      ? '0 8px 32px 0 rgba(0, 212, 255, 0.15)'
                      : '0 8px 32px 0 rgba(16, 185, 129, 0.15)'
                  }}
                >
                  <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white font-bold text-2xl">
                    1
                  </div>
                </div>
                <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Sign Up
                </h3>
                <p className={`text-lg ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Create your free account in seconds. No credit card required to get started.
                </p>
              </div>

              {/* Step 2 */}
              <div className="scroll-animate text-center flex flex-col items-center">
                {/* Glassmorphic Icon */}
                <div
                  className="h-24 w-24 rounded-2xl mb-6 flex items-center justify-center backdrop-blur-xl border shadow-lg"
                  style={{
                    background: isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.8)',
                    borderColor: isDark
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(209, 213, 219, 0.3)',
                    boxShadow: isDark
                      ? '0 8px 32px 0 rgba(59, 130, 246, 0.15)'
                      : '0 8px 32px 0 rgba(59, 130, 246, 0.15)'
                  }}
                >
                  <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-2xl">
                    2
                  </div>
                </div>
                <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Analyze
                </h3>
                <p className={`text-lg ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Use our advanced tools to analyze 100+ cryptocurrencies with real-time data.
                </p>
              </div>

              {/* Step 3 */}
              <div className="scroll-animate text-center flex flex-col items-center">
                {/* Glassmorphic Icon */}
                <div
                  className="h-24 w-24 rounded-2xl mb-6 flex items-center justify-center backdrop-blur-xl border shadow-lg"
                  style={{
                    background: isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.8)',
                    borderColor: isDark
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(209, 213, 219, 0.3)',
                    boxShadow: isDark
                      ? '0 8px 32px 0 rgba(168, 85, 247, 0.15)'
                      : '0 8px 32px 0 rgba(168, 85, 247, 0.15)'
                  }}
                >
                  <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-2xl">
                    3
                  </div>
                </div>
                <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Trade
                </h3>
                <p className={`text-lg ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Make informed decisions with confidence using data-driven insights.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Product Showcase Section */}
        <section className="py-20">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2
                className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
                style={{
                  fontFamily: '"Orbitron", monospace',
                  letterSpacing: '0.05em'
                }}
              >
                See What You're Getting
              </h2>
              <p className={`text-xl ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                Real traders. Real results. Real tools.
              </p>
            </div>

            <div className="space-y-16">
              {/* Opportunity Finder */}
              <div className={`scroll-animate rounded-2xl overflow-hidden ${
                isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white border border-gray-200 shadow-xl'
              }`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="mb-6">
                      <h3 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Bull Market Support
                      </h3>
                      <p className={`text-fade-in text-lg leading-relaxed mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        Know exactly when to buy and when to sell. Our Bull Market Support Band uses AI to identify critical price levels that signal the perfect entry and exit points. Stop guessing—start trading with confidence.
                      </p>
                      <ul className="text-fade-in space-y-3 mb-8">
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            AI-powered market analysis
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            Real-time risk assessment
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            Urgency level indicators
                          </span>
                        </li>
                      </ul>
                      <button
                        onClick={() => handleNavigate('/overview')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-400 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-200"
                      >
                        Explore Opportunities
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-center overflow-hidden">
                    <img
                      src={require('../assets/crypto-logos/Bullmaket.webp')}
                      alt="Bull Market Support Band"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* AI Agents */}
              <div className={`scroll-animate rounded-2xl overflow-hidden ${
                isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white border border-gray-200 shadow-xl'
              }`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="flex items-center justify-center overflow-hidden order-2 lg:order-1 p-8">
                    <div className="w-full aspect-video flex items-center justify-center bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl">
                      <div className="text-center">
                        <Brain className={`h-24 w-24 mx-auto mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                        <div className={`text-xl font-semibold ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          10 AI Agents Working 24/7
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center order-1 lg:order-2">
                    <div className="mb-6">
                      <h3 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        AI Agents Analysis
                      </h3>
                      <p className={`text-fade-in text-lg leading-relaxed mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        Your personal team of AI traders analyzing every coin, every chart, every signal—24/7. They tell you what to buy, when to sell, and how much risk you're taking. In seconds, not hours.
                      </p>
                      <ul className="text-fade-in space-y-3 mb-8">
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            10 specialized AI agents
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            Real-time market intelligence
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            Comprehensive analysis reports
                          </span>
                        </li>
                      </ul>
                      <button
                        onClick={() => handleNavigate('/agent-analysis')}
                        className={`inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-xl hover:scale-105 transition-all duration-200 ${
                          isDark
                            ? 'bg-blue-500/10 text-blue-400 border-2 border-blue-500/50 hover:bg-blue-500/20'
                            : 'bg-blue-50 text-blue-600 border-2 border-blue-200 hover:bg-blue-100'
                        }`}
                      >
                        View AI Agents
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dashboard */}
              <div className={`scroll-animate rounded-2xl overflow-hidden ${
                isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white border border-gray-200 shadow-xl'
              }`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="mb-6">
                      <h3 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Hedge Performers
                      </h3>
                      <p className={`text-fade-in text-lg leading-relaxed mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        Discover coins that go UP when Bitcoin goes DOWN. Perfect for protecting your portfolio and making money in any market. Our dashboard shows you exactly which coins to hedge with—updated in real-time.
                      </p>
                      <ul className="text-fade-in space-y-3 mb-8">
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            Market overview at a glance
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            Multiple timeframe analysis
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            Real-time market metrics
                          </span>
                        </li>
                      </ul>
                      <button
                        onClick={() => handleNavigate('/overview')}
                        className={`inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-xl hover:scale-105 transition-all duration-200 ${
                          isDark
                            ? 'bg-purple-500/10 text-purple-400 border-2 border-purple-500/50 hover:bg-purple-500/20'
                            : 'bg-purple-50 text-purple-600 border-2 border-purple-200 hover:bg-purple-100'
                        }`}
                      >
                        Open Dashboard
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-center overflow-hidden">
                    <img
                      src={require('../assets/crypto-logos/Hedgeperformance.webp')}
                      alt="Hedge Performers Analytics"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2
                className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
                style={{
                  fontFamily: '"Orbitron", monospace',
                  letterSpacing: '0.05em'
                }}
              >
                Trusted by Crypto Traders
              </h2>
              <p className={`text-xl ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                Real-time data and analysis you can rely on
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className={`text-center p-8 rounded-2xl ${
                isDark ? 'bg-zinc-900/50 border border-white/5' : 'bg-gray-50 border border-gray-100'
              }`}>
                <div className="text-5xl font-bold mb-2 bg-gradient-to-r from-emerald-400 to-cyan-500 bg-clip-text text-transparent">
                  100+
                </div>
                <div className={`text-lg font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Coins Tracked
                </div>
              </div>

              <div className={`text-center p-8 rounded-2xl ${
                isDark ? 'bg-zinc-900/50 border border-white/5' : 'bg-gray-50 border border-gray-100'
              }`}>
                <div className="text-5xl font-bold mb-2 bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                  4hr
                </div>
                <div className={`text-lg font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Update Frequency
                </div>
              </div>

              <div className={`text-center p-8 rounded-2xl ${
                isDark ? 'bg-zinc-900/50 border border-white/5' : 'bg-gray-50 border border-gray-100'
              }`}>
                <div className="text-5xl font-bold mb-2 bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                  10+
                </div>
                <div className={`text-lg font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Technical Indicators
                </div>
              </div>

              <div className={`text-center p-8 rounded-2xl ${
                isDark ? 'bg-zinc-900/50 border border-white/5' : 'bg-gray-50 border border-gray-100'
              }`}>
                <div className="text-5xl font-bold mb-2 bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                  5D
                </div>
                <div className={`text-lg font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Scoring Dimensions
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className={`py-16 border-t ${
          isDark ? 'bg-zinc-950 border-white/10' : 'bg-white border-gray-200'
        }`}>
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
              {/* Brand */}
              <div className="col-span-1">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl">
                    IV
                  </div>
                  <span className={`font-bold text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    InvestVerse.AI
                  </span>
                </div>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  Advanced cryptocurrency analysis platform for serious traders
                </p>
              </div>

              {/* Product */}
              <div>
                <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Product
                </h4>
                <ul className="space-y-3">
                  <li>
                    <span className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      Opportunity Finder (Coming Soon)
                    </span>
                  </li>
                  <li>
                    <span className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      AI Agents (Available Now)
                    </span>
                  </li>
                  <li>
                    <span className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      Dashboard (Coming Soon)
                    </span>
                  </li>
                </ul>
              </div>

              {/* Resources */}
              <div>
                <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Resources
                </h4>
                <ul className="space-y-3">
                  <li>
                    <a
                      href="/docs"
                      className={`text-sm transition-colors hover:text-blue-500 ${isDark ? 'text-white/60' : 'text-gray-600'}`}
                    >
                      Documentation
                    </a>
                  </li>
                  <li>
                    <a
                      href="/api"
                      className={`text-sm transition-colors hover:text-blue-500 ${isDark ? 'text-white/60' : 'text-gray-600'}`}
                    >
                      API Reference
                    </a>
                  </li>
                  <li>
                    <a
                      href="/blog"
                      className={`text-sm transition-colors hover:text-blue-500 ${isDark ? 'text-white/60' : 'text-gray-600'}`}
                    >
                      Blog
                    </a>
                  </li>
                  <li>
                    <a
                      href="/changelog"
                      className={`text-sm transition-colors hover:text-blue-500 ${isDark ? 'text-white/60' : 'text-gray-600'}`}
                    >
                      Changelog
                    </a>
                  </li>
                </ul>
              </div>

              {/* Company */}
              <div>
                <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Company
                </h4>
                <ul className="space-y-3">
                  <li>
                    <a
                      href="/about"
                      className={`text-sm transition-colors hover:text-blue-500 ${isDark ? 'text-white/60' : 'text-gray-600'}`}
                    >
                      About Us
                    </a>
                  </li>
                  <li>
                    <a
                      href="/pricing"
                      className={`text-sm transition-colors hover:text-blue-500 ${isDark ? 'text-white/60' : 'text-gray-600'}`}
                    >
                      Pricing
                    </a>
                  </li>
                  <li>
                    <a
                      href="/privacy"
                      className={`text-sm transition-colors hover:text-blue-500 ${isDark ? 'text-white/60' : 'text-gray-600'}`}
                    >
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a
                      href="/terms"
                      className={`text-sm transition-colors hover:text-blue-500 ${isDark ? 'text-white/60' : 'text-gray-600'}`}
                    >
                      Terms of Service
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className={`pt-8 border-t text-center ${
              isDark ? 'border-white/10' : 'border-gray-200'
            }`}>
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                © {new Date().getFullYear()} InvestVerse.AI. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authModalMode}
        redirectTo="/agent-analysis"
      />
    </div>
  );
};

export default NewHome;
